import React, { useState, useEffect } from 'react';
import API from '../../../services/api';
import { API_BASE_URL } from '../../../constants/config';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

const getServerRootUrl = () => API_BASE_URL.replace(/\/api\/?$/, '');

const DeliveryChallan = () => {
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [cities, setCities] = useState([]);
    const [fromLoc, setFromLoc] = useState('');
    const [toLoc, setToLoc] = useState('');
    
    // Search vs Manual Bike Entry States
    const [isManualEntry, setIsManualEntry] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedBike, setSelectedBike] = useState(null);

    // Manual Bike Form States
    const [manualBike, setManualBike] = useState({
        model_name: '',
        power: '',
        engine_number: '',
        chassis_number: ''
    });

    const [buyerName, setBuyerName] = useState('');
    const [buyerCnic, setBuyerCnic] = useState('');
    const serverRoot = getServerRootUrl();

    useEffect(() => {
        fetchCompanies();
        fetchCities();
    }, []);

    const fetchCompanies = async () => {
        try {
            const res = await API.get('/v1/others/companies');
            if (res.data.success && res.data.data.length > 0) {
                setCompanies(res.data.data);
                setSelectedCompanyId(res.data.data[0].id);
            }
        } catch (err) {
            console.error('Error fetching companies:', err);
        }
    };

    const fetchCities = async () => {
        try {
            const res = await API.get('/v1/cities');
            if (res.data.success && res.data.data.length > 0) {
                setCities(res.data.data);
                setFromLoc(res.data.data[0].name);
                setToLoc(res.data.data[1] ? res.data.data[1].name : res.data.data[0].name);
            }
        } catch (err) {
            console.error('Error fetching cities:', err);
        }
    };

    const selectedCompany = companies.find(c => c.id === parseInt(selectedCompanyId)) || companies[0];

    const handleCnicChange = (e) => {
        let val = e.target.value.replace(/\D/g, ''); 
        if (val.length > 13) val = val.substring(0, 13);
        
        let formatted = val;
        if (val.length > 5 && val.length <= 12) {
            formatted = `${val.slice(0, 5)}-${val.slice(5)}`;
        } else if (val.length > 12) {
            formatted = `${val.slice(0, 5)}-${val.slice(5, 12)}-${val.slice(12)}`;
        }
        setBuyerCnic(formatted);
    };

    const handleSearch = async (val) => {
        setSearchQuery(val);
        if (val.length < 2) {
            setSearchResults([]);
            return;
        }
        try {
            const res = await API.get(`/v1/others/bikes/search?query=${val}`);
            if (res.data.success) setSearchResults(res.data.data);
        } catch (err) {
            console.error(err);
        }
    };

    const selectBike = (bike) => {
        setSelectedBike(bike);
        setSearchQuery(`Eng: ${bike.engine_number} | Chassis: ${bike.chassis_number}`);
        setSearchResults([]);
        if (bike.customer_name) {
            setBuyerName(bike.customer_name);
        }
        if (bike.customer_cnic) {
            const rawCnic = bike.customer_cnic.replace(/\D/g, '');
            if (rawCnic.length === 13) {
                setBuyerCnic(`${rawCnic.slice(0, 5)}-${rawCnic.slice(5, 12)}-${rawCnic.slice(12)}`);
            } else {
                setBuyerCnic(bike.customer_cnic);
            }
        }
    };

    const loadImageAsBase64 = (url) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = () => resolve(null);
            img.src = url;
        });
    };

    const getActiveBikeDetails = () => {
        if (isManualEntry) {
            return {
                id: null,
                model_name: manualBike.model_name || 'CHOPER',
                power: manualBike.power ? `${manualBike.power}CC` : '250CC',
                engine_number: manualBike.engine_number || 'N/A',
                chassis_number: manualBike.chassis_number || 'N/A'
            };
        }
        if (!selectedBike) return { model_name: '', power: '', engine_number: '', chassis_number: '' };
        return {
            id: selectedBike.id,
            model_name: selectedBike.model_name || 'CHOPER',
            power: selectedBike.power ? `${selectedBike.power}CC` : (selectedBike.cc ? `${selectedBike.cc}CC` : '70CC'),
            engine_number: selectedBike.engine_number || 'N/A',
            chassis_number: selectedBike.chassis_number || 'N/A'
        };
    };

    const handleSaveChallan = async () => {
        const activeBike = getActiveBikeDetails();
        if (!selectedCompany || (!isManualEntry && !selectedBike) || (isManualEntry && (!manualBike.engine_number || !manualBike.chassis_number))) {
            alert('Please provide complete company and bike details!');
            return;
        }
        try {
            const payload = {
                company_id: selectedCompany.id,
                bike_master_id: activeBike.id,
                buyer_name: buyerName,
                cnic: buyerCnic,
                from_location: fromLoc,
                to_location: toLoc,
                challan_date: new Date().toISOString().split('T')[0]
            };
            const res = await API.post('/v1/others/delivery-challans', payload);
            if (res.data.success) {
                alert(`Delivery Challan ${res.data.challan_no || 'saved'} saved to database history!`);
            }
        } catch (err) {
            alert('Error saving delivery challan');
        }
    };

    const downloadPDF = async () => {
        const activeBike = getActiveBikeDetails();
        if (!selectedCompany || (!isManualEntry && !selectedBike)) return alert('Select or enter bike details first!');
        
        const doc = new jsPDF();
        let startY = 15;

        if (selectedCompany.header_logo) {
            const logoUrl = `${serverRoot}/uploads/${selectedCompany.header_logo}`;
            const imgData = await loadImageAsBase64(logoUrl);
            if (imgData) {
                doc.addImage(imgData, 'PNG', 15, startY, 180, 22);
                startY += 26;
            }
        }

        doc.setFontSize(15);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(15, 23, 42);
        doc.text(selectedCompany.name.toUpperCase(), 105, startY, { align: 'center' });

        startY += 8;
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(50, 50, 50);
        const formattedDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
        doc.text(`Date: ${formattedDate}`, 15, startY);

        startY += 10;
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(2, 132, 199);
        doc.text('AUTHORITY LETTER FOR DELIVERY / TRANSPORTATION OF MOTORCYCLE', 105, startY, { align: 'center' });

        startY += 10;
        doc.setTextColor(40, 40, 40);
        doc.setFontSize(9.5);
        doc.setFont("helvetica", "normal");
        const p1 = `This is to certify that ${selectedCompany.name} hereby authorizes the delivery / transportation of the following motorcycle:`;
        doc.text(doc.splitTextToSize(p1, 180), 14, startY);

        startY += 10;
        const startX = 14;
        const colWidths = [50, 35, 45, 50];
        const rowHeight = 9;
        const tableWidth = colWidths.reduce((a, b) => a + b, 0);

        doc.setFillColor(2, 132, 199);
        doc.rect(startX, startY, tableWidth, rowHeight, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9.5);
        let currentX = startX;
        const headers = ['Bike Detail', 'Power', 'Engine #', 'Chassis #'];
        headers.forEach((header, index) => {
            doc.text(header, currentX + (colWidths[index] / 2), startY + 6, { align: 'center' });
            currentX += colWidths[index];
        });

        startY += rowHeight;
        doc.setFillColor(248, 250, 252);
        doc.rect(startX, startY, tableWidth, rowHeight, 'F');
        doc.setDrawColor(203, 213, 225);
        doc.rect(startX, startY - rowHeight, tableWidth, rowHeight * 2);
        
        doc.setTextColor(15, 23, 42);
        doc.setFont("helvetica", "normal");
        currentX = startX;
        const rowData = [
            activeBike.model_name || 'CHOPER',
            activeBike.power || '70CC',
            activeBike.engine_number || 'N/A',
            activeBike.chassis_number || 'N/A'
        ];
        rowData.forEach((data, index) => {
            doc.text(String(data), currentX + (colWidths[index] / 2), startY + 6, { align: 'center' });
            currentX += colWidths[index];
        });

        startY += 16;
        doc.setFont("helvetica", "bold");
        doc.setTextColor(15, 23, 42);
        doc.text(`Route: From ${fromLoc} To ${toLoc}`, 105, startY, { align: 'center' });

        startY += 10;
        doc.setFont("helvetica", "normal");
        doc.setTextColor(60, 60, 60);
        const p2 = `The above-mentioned motorcycle belongs to ${selectedCompany.name}, and the bearer of this letter is duly authorized to transport and deliver the said vehicle to the specified destination.`;
        doc.text(doc.splitTextToSize(p2, 180), 14, startY);

        startY += 16;
        const p3 = `This authority letter is issued for delivery and transportation purposes only. For any verification or further information, please feel free to contact us.`;
        doc.text(doc.splitTextToSize(p3, 180), 14, startY);

        startY += 12;
        doc.setFont("helvetica", "bold");
        doc.text(`Contact No.: ${selectedCompany.phone || '0321-5400001'}`, 14, startY);

        startY += 12;
        doc.setFont("helvetica", "normal");
        doc.text('Sincerely,', 14, startY);
        startY += 14;
        doc.setFont("helvetica", "bold");
        doc.text('Authorized Signatory', 14, startY);
        startY += 6;
        doc.text(selectedCompany.name.toUpperCase(), 14, startY);

        if (selectedCompany.footer_logo) {
            const footerUrl = `${serverRoot}/uploads/${selectedCompany.footer_logo}`;
            const footerImgData = await loadImageAsBase64(footerUrl);
            if (footerImgData) {
                doc.addImage(footerImgData, 'PNG', 15, 260, 180, 18);
            }
        }

        doc.save(`Authority_Letter_${activeBike.chassis_number}.pdf`);
    };

    const downloadExcel = () => {
        const activeBike = getActiveBikeDetails();
        if (!selectedCompany || (!isManualEntry && !selectedBike)) return alert('Select or enter bike details first!');
        const formattedDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
        
        const wsData = [
            [selectedCompany.name.toUpperCase()],
            [],
            ["Date:", formattedDate],
            [],
            ["AUTHORITY LETTER FOR DELIVERY / TRANSPORTATION OF MOTORCYCLE"],
            [],
            [`This is to certify that ${selectedCompany.name} hereby authorizes the delivery / transportation of the following motorcycle:`],
            [],
            ["Bike Detail", "Power", "Engine #", "Chassis #"],
            [activeBike.model_name || 'CHOPER', activeBike.power || '70CC', activeBike.engine_number, activeBike.chassis_number],
            [],
            ["Route:", `From ${fromLoc} To ${toLoc}`],
            [],
            [`The above-mentioned motorcycle belongs to ${selectedCompany.name}, and the bearer of this letter is duly authorized to transport and deliver the said vehicle to the specified destination.`],
            [],
            ["This authority letter is issued for delivery and transportation purposes only. For any verification or further information, please feel free to contact us."],
            [],
            ["Contact No.:", selectedCompany.phone || "0321-5400001"],
            [],
            ["Sincerely,"],
            [],
            ["Authorized Signatory"],
            [selectedCompany.name.toUpperCase()],
            [],
            ["Address:", `${selectedCompany.address || ''}. Ph: ${selectedCompany.phone || ''}`]
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = [{ wch: 35 }, { wch: 15 }, { wch: 25 }, { wch: 30 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Delivery Challan");
        XLSX.writeFile(wb, `Authority_Letter_${activeBike.chassis_number}.xlsx`);
    };

    const activeBike = getActiveBikeDetails();
    const formattedDisplayDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');

    return (
        <div style={styles.card}>
            <h3 style={styles.title}>📦 Delivery Challan / Authority Letter</h3>

            <div style={styles.gridForm}>
                <div>
                    <label style={styles.label}>Select Company Name (From Database)</label>
                    <select value={selectedCompanyId} onChange={(e) => setSelectedCompanyId(e.target.value)} style={styles.input}>
                        {companies.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label style={styles.label}>Buyer Name</label>
                    <input 
                        type="text" 
                        placeholder="Enter Buyer Name..." 
                        value={buyerName}
                        onChange={(e) => setBuyerName(e.target.value)}
                        style={styles.input}
                    />
                </div>
            </div>

            <div style={{ marginTop: '15px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                    <label style={styles.label}>Customer / Buyer CNIC (Auto Dash)</label>
                    <input 
                        type="text" 
                        placeholder="32203-5843348-9" 
                        value={buyerCnic}
                        onChange={handleCnicChange}
                        style={styles.input}
                    />
                </div>
                <div>
                    <label style={styles.label}>From City</label>
                    <select value={fromLoc} onChange={(e) => setFromLoc(e.target.value)} style={styles.input}>
                        {cities.map(city => (
                            <option key={city.id} value={city.name}>{city.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div style={{ marginTop: '15px' }}>
                <label style={styles.label}>To City</label>
                <select value={toLoc} onChange={(e) => setToLoc(e.target.value)} style={styles.input}>
                    {cities.map(city => (
                        <option key={city.id} value={city.name}>{city.name}</option>
                    ))}
                </select>
            </div>

            {/* Toggle Search vs Manual Entry */}
            <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={styles.label}>Bike Details Selection</label>
                <button 
                    type="button" 
                    onClick={() => { setIsManualEntry(!isManualEntry); setSelectedBike(null); }} 
                    style={styles.toggleBtn}
                >
                    {isManualEntry ? '🔍 Switch to Search Bike' : '➕ Add Bike Manually'}
                </button>
            </div>

            {isManualEntry ? (
                <div style={{ ...styles.gridForm, marginTop: '10px', background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                    <div>
                        <label style={styles.label}>Model Name</label>
                        <input type="text" placeholder="e.g. CHOPER / Thunder" value={manualBike.model_name} onChange={(e) => setManualBike({...manualBike, model_name: e.target.value})} style={styles.input} />
                    </div>
                    <div>
                        <label style={styles.label}>Power (CC)</label>
                        <input type="text" placeholder="e.g. 70 or 125" value={manualBike.power} onChange={(e) => setManualBike({...manualBike, power: e.target.value})} style={styles.input} />
                    </div>
                    <div>
                        <label style={styles.label}>Engine Number</label>
                        <input type="text" placeholder="Engine #" value={manualBike.engine_number} onChange={(e) => setManualBike({...manualBike, engine_number: e.target.value})} style={styles.input} />
                    </div>
                    <div>
                        <label style={styles.label}>Chassis Number</label>
                        <input type="text" placeholder="Chassis #" value={manualBike.chassis_number} onChange={(e) => setManualBike({...manualBike, chassis_number: e.target.value})} style={styles.input} />
                    </div>
                </div>
            ) : (
                <div style={{ position: 'relative', margin: '15px 0' }}>
                    <label style={styles.label}>Search Bike by Engine / Chassis Number</label>
                    <input 
                        type="text" 
                        placeholder="Enter Chassis # or Engine #..." 
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        style={styles.input}
                    />
                    {searchResults.length > 0 && (
                        <div style={styles.dropdown}>
                            {searchResults.map(b => (
                                <div key={b.id} style={styles.dropdownItem} onClick={() => selectBike(b)}>
                                    <strong>{b.model_name}</strong> ({b.cc ? `${b.cc}CC` : 'N/A'}) - Eng: {b.engine_number} | Chassis: {b.chassis_number}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {(selectedBike || isManualEntry) && (
                <div style={styles.letterContainer}>
                    {selectedCompany.header_logo && (
                        <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                            <img src={`${serverRoot}/uploads/${selectedCompany.header_logo}`} alt="Header Logo" style={{ maxHeight: '60px', objectFit: 'contain' }} />
                        </div>
                    )}

                    <h2 style={{ textAlign: 'center', margin: '0 0 5px 0', textTransform: 'uppercase', color: '#0f172a' }}>{selectedCompany.name}</h2>
                    <div style={{ textAlign: 'right', marginBottom: '15px', fontWeight: '500', color: '#475569' }}>
                        Date: {formattedDisplayDate}
                    </div>

                    <h4 style={{ textAlign: 'center', margin: '15px 0', textTransform: 'uppercase', color: '#0284c7' }}>
                        AUTHORITY LETTER FOR DELIVERY / TRANSPORTATION OF MOTORCYCLE
                    </h4>

                    <p style={{ lineHeight: '1.6' }}>
                        This is to certify that <strong>{selectedCompany.name}</strong> hereby authorizes the delivery / transportation of the following motorcycle:
                    </p>

                    <table style={styles.table}>
                        <thead>
                            <tr style={{ background: '#0284c7', color: '#fff' }}>
                                <th style={styles.thTdHeader}>Bike Detail</th>
                                <th style={styles.thTdHeader}>Power</th>
                                <th style={styles.thTdHeader}>Engine #</th>
                                <th style={styles.thTdHeader}>Chassis #</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={styles.thTd}>{activeBike.model_name}</td>
                                <td style={styles.thTd}>{activeBike.power}</td>
                                <td style={styles.thTd}>{activeBike.engine_number}</td>
                                <td style={styles.thTd}>{activeBike.chassis_number}</td>
                            </tr>
                        </tbody>
                    </table>

                    <p style={{ textAlign: 'center', fontWeight: 'bold', margin: '15px 0', color: '#0f172a' }}>
                        From {fromLoc} To {toLoc}
                    </p>

                    <p style={{ lineHeight: '1.6' }}>
                        The above-mentioned motorcycle belongs to <strong>{selectedCompany.name}</strong>, and the bearer of this letter is duly authorized to transport and deliver the said vehicle to the specified destination.
                    </p>

                    <p style={{ lineHeight: '1.6' }}>
                        This authority letter is issued for delivery and transportation purposes only. For any verification or further information, please feel free to contact us.
                    </p>

                    <p style={{ margin: '15px 0', fontWeight: '500' }}>(Contact No.: {selectedCompany.phone || '0321-5400001'})</p>

                    <p style={{ marginTop: '30px' }}>Sincerely,</p>

                    <div style={{ marginTop: '30px' }}>
                        <p style={{ margin: 0, fontWeight: 'bold' }}>Authorized Signatory</p>
                        <p style={{ margin: 0, fontWeight: 'bold', textTransform: 'uppercase' }}>{selectedCompany.name}</p>
                    </div>

                    {selectedCompany.footer_logo ? (
                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                            <img src={`${serverRoot}/uploads/${selectedCompany.footer_logo}`} alt="Footer Logo" style={{ maxHeight: '45px', objectFit: 'contain' }} />
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', marginTop: '40px', fontSize: '12px', color: '#64748b', borderTop: '1px solid #e2e8f0', paddingTop: '10px' }}>
                            {selectedCompany.address || 'N-21, Circular Road, Rawalpindi, Pakistan'}. Ph: {selectedCompany.phone || '+92-321-5400001'}.
                        </div>
                    )}
                </div>
            )}

            <div style={styles.actionRow}>
                <button onClick={handleSaveChallan} style={styles.saveBtn}>💾 Save to Database</button>
                <button onClick={downloadPDF} style={styles.pdfBtn}>📄 Download PDF</button>
                <button onClick={downloadExcel} style={styles.excelBtn}>📊 Download Excel</button>
            </div>
        </div>
    );
};

const styles = {
    card: { background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' },
    title: { margin: '0 0 15px 0', fontSize: '18px', color: '#0f172a' },
    gridForm: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' },
    label: { display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '5px', color: '#475569' },
    input: { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' },
    toggleBtn: { background: '#0284c7', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
    dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', zIndex: 10, maxHeight: '150px', overflowY: 'auto' },
    dropdownItem: { padding: '10px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '13px' },
    letterContainer: { marginTop: '20px', border: '1px solid #cbd5e1', padding: '25px', borderRadius: '8px', background: '#fff', color: '#0f172a' },
    table: { width: '100%', borderCollapse: 'collapse', margin: '15px 0' },
    thTdHeader: { border: '1px solid #0284c7', padding: '10px', textAlign: 'center', fontSize: '13px', background: '#0284c7', color: '#fff' },
    thTd: { border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '13px', fontWeight: 'bold', color: '#0f172a' },
    actionRow: { display: 'flex', gap: '10px', marginTop: '20px' },
    saveBtn: { background: '#0f172a', color: '#fff', padding: '10px 18px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' },
    pdfBtn: { background: '#ef4444', color: '#fff', padding: '10px 18px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' },
    excelBtn: { background: '#10b981', color: '#fff', padding: '10px 18px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }
};

export default DeliveryChallan;